import React from 'react';
import { BarChart3, Check, Plus, Target, Trash2 } from 'lucide-react';

const GoalsTab = ({ ctx }) => {
  const { goalsData, API_URL, getToken, showAddGoal, goalForm, setShowAddGoal, setGoalForm, setGoalsData } = ctx;

  return (
<div data-testid="goals-tab">
  <div className="flex items-center justify-between mb-1">
    <h1 className="text-xl font-bold" style={{ color: '#1A1209' }}>Goals & OKRs</h1>
    <button onClick={() => setShowAddGoal(!showAddGoal)}
      className="px-4 py-2 rounded-lg text-xs font-semibold text-white flex items-center gap-1.5"
      style={{ backgroundColor: '#F5831F' }} data-testid="add-goal-btn">
      <Plus size={13} /> Set New Goal
    </button>
  </div>
  <p className="text-xs mb-5" style={{ color: '#999' }}>
    Set measurable targets for your team and track progress in real-time. Goals automatically pull from live platform metrics.
  </p>

  {goalsData?.demo_data && (
    <div className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium mb-4" style={{ backgroundColor: '#EFF6FF', color: '#1E40AF', border: '1px solid #BFDBFE' }}>
      <BarChart3 size={14} />
      Sample goals — create your own targets and they&apos;ll track against live data
    </div>
  )}

  {/* Create goal form */}
  {showAddGoal && (
    <div className="bg-white rounded-xl p-5 mb-5" style={{ border: '2px solid #F5831F33' }} data-testid="goal-form">
      <h3 className="text-sm font-bold mb-3" style={{ color: '#1A1209' }}>New Goal</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
        <input value={goalForm.title} onChange={e => setGoalForm(p => ({ ...p, title: e.target.value }))}
          placeholder="Goal title *" className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="goal-title" />
        <select value={goalForm.metric_type} onChange={e => setGoalForm(p => ({ ...p, metric_type: e.target.value }))}
          className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="goal-metric">
          <option value="ocean_completion">OCEAN Completion (%)</option>
          <option value="sessions_per_member">Sessions per Member</option>
          <option value="pulse_score">Pulse Score (/5)</option>
          <option value="engagement_rate">Engagement Rate (%)</option>
          <option value="badge_earn_rate">Badge Earn Rate (%)</option>
        </select>
        <input value={goalForm.target_value} onChange={e => setGoalForm(p => ({ ...p, target_value: e.target.value }))}
          placeholder="Target value *" type="number" className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="goal-target" />
        <input value={goalForm.deadline} onChange={e => setGoalForm(p => ({ ...p, deadline: e.target.value }))}
          type="date" className="px-3 py-2 border rounded-lg text-sm" style={{ borderColor: '#E0E0E0' }} data-testid="goal-deadline" />
      </div>
      <textarea value={goalForm.description} onChange={e => setGoalForm(p => ({ ...p, description: e.target.value }))}
        placeholder="Description (optional)" rows={2}
        className="w-full px-3 py-2 border rounded-lg text-sm mb-3" style={{ borderColor: '#E0E0E0' }} data-testid="goal-desc" />
      <div className="flex gap-2">
        <button onClick={async () => {
          if (!goalForm.title.trim() || !goalForm.target_value) return;
          try {
            const res = await fetch(`${API_URL}/api/corporate/goals`, {
              method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` },
              body: JSON.stringify({ ...goalForm, target_value: parseFloat(goalForm.target_value) }),
            });
            if (res.ok) {
              setGoalForm({ title: '', metric_type: 'ocean_completion', target_value: '', deadline: '', description: '' });
              setShowAddGoal(false);
              const r2 = await fetch(`${API_URL}/api/corporate/goals`, { headers: { Authorization: `Bearer ${getToken()}` } });
              if (r2.ok) setGoalsData(await r2.json());
            }
          } catch {}
        }} disabled={!goalForm.title.trim() || !goalForm.target_value}
          className="px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50 flex items-center gap-1.5"
          style={{ backgroundColor: '#2EAA5C' }} data-testid="save-goal-btn">
          <Check size={14} /> Create Goal
        </button>
        <button onClick={() => setShowAddGoal(false)} className="px-4 py-2 rounded-lg text-sm" style={{ backgroundColor: '#F0F0F0', color: '#666' }}>Cancel</button>
      </div>
    </div>
  )}

  {/* Goals grid */}
  {goalsData?.goals?.length > 0 ? (
    <div className="space-y-4">
      {goalsData.goals.map(goal => {
        const pct = goal.target_value > 0 ? Math.min(Math.round((goal.current_value / goal.target_value) * 100), 100) : 0;
        const statusColors = {
          on_track: { bg: '#F0FDF4', border: '#BBF7D0', text: '#166534', bar: '#10B981', label: 'On Track' },
          at_risk: { bg: '#FFFBEB', border: '#FDE68A', text: '#92400E', bar: '#F59E0B', label: 'At Risk' },
          behind: { bg: '#FEF2F2', border: '#FECACA', text: '#991B1B', bar: '#EF4444', label: 'Behind' },
          not_started: { bg: '#F8F7F4', border: '#E8E8E8', text: '#666', bar: '#D1D5DB', label: 'Not Started' },
        };
        const s = statusColors[goal.status] || statusColors.not_started;
        const daysLeft = goal.deadline ? Math.max(0, Math.ceil((new Date(goal.deadline) - new Date()) / (1000 * 60 * 60 * 24))) : null;
        return (
          <div key={goal.goal_id} className="bg-white rounded-xl p-5 transition-all hover:shadow-sm group" style={{ border: `1px solid ${s.border}` }} data-testid={`goal-${goal.goal_id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h4 className="text-sm font-bold" style={{ color: '#1A1209' }}>{goal.title}</h4>
                  <span className="px-2 py-0.5 rounded-full text-[9px] font-bold" style={{ backgroundColor: s.bg, color: s.text, border: `1px solid ${s.border}` }}>
                    {s.label}
                  </span>
                  {goal.is_demo && <span className="px-1.5 py-0.5 rounded text-[8px] font-medium" style={{ backgroundColor: '#F0F0F0', color: '#999' }}>DEMO</span>}
                </div>
                {goal.description && <p className="text-[10px]" style={{ color: '#999' }}>{goal.description}</p>}
              </div>
              <div className="flex items-center gap-2">
                {daysLeft !== null && (
                  <span className="text-[10px] font-medium px-2 py-0.5 rounded-lg" style={{ backgroundColor: daysLeft < 14 ? '#FEF2F2' : '#F8F7F4', color: daysLeft < 14 ? '#DC2626' : '#999' }}>
                    {daysLeft === 0 ? 'Due today' : `${daysLeft}d left`}
                  </span>
                )}
                {!goal.is_demo && (
                  <button onClick={async () => {
                    if (!window.confirm(`Delete "${goal.title}"?`)) return;
                    await fetch(`${API_URL}/api/corporate/goals/${goal.goal_id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${getToken()}` } });
                    const r2 = await fetch(`${API_URL}/api/corporate/goals`, { headers: { Authorization: `Bearer ${getToken()}` } });
                    if (r2.ok) setGoalsData(await r2.json());
                  }} className="p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-gray-100" data-testid={`delete-goal-${goal.goal_id}`}>
                    <Trash2 size={12} style={{ color: '#DC2626' }} />
                  </button>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-semibold" style={{ color: '#666' }}>
                    {goal.current_value}{goal.unit} of {goal.target_value}{goal.unit}
                  </span>
                  <span className="text-lg font-black" style={{ color: s.bar }}>{pct}%</span>
                </div>
                <div className="w-full h-3 rounded-full" style={{ backgroundColor: '#F0F0F0' }}>
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: s.bar }} />
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  ) : (
    <div className="bg-white rounded-xl p-8 text-center" style={{ border: '1px solid #E8E8E8' }}>
      <Target size={32} style={{ color: '#DDD' }} className="mx-auto mb-3" />
      <p className="text-sm" style={{ color: '#999' }}>No goals set yet. Create targets and track your team&apos;s progress.</p>
    </div>
  )}
</div>
  );
};

export default GoalsTab;
